import type { MouseEvent, ReactNode, Ref, SyntheticEvent } from 'react';
import { createContext, useContext, useEffect } from 'react';
import { Provider } from 'react-aria-components/slots';
import { FocusableProvider } from '@react-aria/focus';
import { ClearPressResponder } from '@react-aria/interactions';
import { useObjectRef } from '@react-aria/utils';
import type { KeyboardEvents, PressEvents } from '@react-types/shared';

interface InteractionProps extends KeyboardEvents, PressEvents {}

interface PerceivableProps {
	children?: ReactNode;
	isDisabled?: boolean;
}

const PerceivableContext = createContext<InteractionProps>({});

const clearedPressAndKeyProps = {
	onPress: undefined,
	onPressStart: undefined,
	onPressEnd: undefined,
	onPressChange: undefined,
	onPressUp: undefined,
	onKeyDown: undefined,
	onKeyUp: undefined,
	onClick: undefined,
	href: undefined,
} as const;

const clearedInteractionProps = { ...clearedPressAndKeyProps, type: 'button' } as const;

const cancelClick = (event: MouseEvent) => {
	event.preventDefault();
	event.stopPropagation();
};

const stopEvent = (event: SyntheticEvent) => {
	event.stopPropagation();
};

const cancelActivationKey = (event: KeyboardEvent) => {
	if (event.key === 'Enter' || event.key === ' ') {
		event.preventDefault();
		event.stopPropagation();
	}
};

interface TooltipTriggerContextValue {
	/** The element tooltips should position against when it differs from the focusable trigger. */
	anchor: Element | null;
	setAnchor: (anchor: Element | null) => void;
}

/** Provided by `TooltipTrigger` to everything inside it. */
const TooltipTriggerContext = createContext<TooltipTriggerContextValue | null>(null);

/**
 * Turns on disabled tooltip triggers below it: a disabled `Button`, `IconButton`, `ToggleButton`,
 * `ToggleIconButton` or `Switch` placed directly in a `TooltipTrigger` stays focusable and hoverable so the
 * tooltip can explain why it is disabled. Off by default, so those controls stay natively disabled.
 */
const DisabledTooltipTriggerContext = createContext(false);

/** Provided by `TooltipTrigger` only around its direct trigger child, so nested controls are left alone. */
const IsTooltipTriggerContext = createContext(false);

/**
 * Whether a control should stay perceivable instead of disabled: it is disabled and is the direct trigger of
 * a `TooltipTrigger`, so the tooltip is the only way to learn why.
 */
const useIsDisabledTooltipTrigger = (isDisabled: boolean | undefined) =>
	useContext(IsTooltipTriggerContext) && isDisabled === true;

/**
 * Props that turn a disabled button into a perceivable one: focusable and hoverable, announced as
 * disabled, with every press, key, click and submit behavior removed.
 */
const disabledTooltipTriggerProps = {
	...clearedPressAndKeyProps,
	onClickCapture: cancelClick,
	isDisabled: false,
	'aria-disabled': true,
	'data-lp': '',
} as const;

/**
 * `disabledTooltipTriggerProps` for toggle buttons, which toggle on press even without an `onPress`, so the
 * press itself is stopped before it reaches the button.
 */
const disabledToggleTooltipTriggerProps = {
	...disabledTooltipTriggerProps,
	onPointerDownCapture: stopEvent,
	onMouseDownCapture: stopEvent,
	onTouchStartCapture: stopEvent,
} as const;

/**
 * Stops Enter and Space from pressing a disabled toggle button. React Aria does not forward key handlers to the
 * DOM, so the listener is added to the element directly.
 */
const useCancelActivationKeys = <E extends HTMLElement>(ref: Ref<E | null>, isEnabled: boolean) => {
	const objectRef = useObjectRef(ref);
	useEffect(() => {
		const element = objectRef.current;
		if (!isEnabled || !element) {
			return undefined;
		}
		element.addEventListener('keydown', cancelActivationKey);
		return () => element.removeEventListener('keydown', cancelActivationKey);
	}, [isEnabled, objectRef]);
	return objectRef;
};

const Perceivable = ({ children, isDisabled = true }: PerceivableProps) => {
	return isDisabled ? (
		<Provider values={[[PerceivableContext, clearedInteractionProps]]}>
			<FocusableProvider aria-disabled="true" data-lp="">
				<ClearPressResponder>{children}</ClearPressResponder>
			</FocusableProvider>
		</Provider>
	) : (
		children
	);
};

export {
	cancelClick,
	DisabledTooltipTriggerContext,
	disabledToggleTooltipTriggerProps,
	disabledTooltipTriggerProps,
	IsTooltipTriggerContext,
	Perceivable,
	PerceivableContext,
	TooltipTriggerContext,
	useCancelActivationKeys,
	useIsDisabledTooltipTrigger,
};
export type { TooltipTriggerContextValue };
export type { PerceivableProps };
